import React, { useState, useEffect } from "react";
import { useNavigate } from 'react-router-dom';
import { getScrapperData } from "../api";
import ExcelJS from "exceljs";

// ---------------- CLEAN TEXT FUNCTION ----------------
const cleanText = (text) => {
  if (!text) return "";

  return String(text)
    // remove emojis & special unicode icons
    .replace(
      /([\u2700-\u27BF]|[\uE000-\uF8FF]|[\uD800-\uDBFF][\uDC00-\uDFFF]|[\u2011-\u26FF]|[\uFE0F])/g,
      ""
    )
    // remove HTML tags
    .replace(/<[^>]+>/g, "")
    // remove common LinkedIn symbols, bullets, arrows, ticks
    .replace(/&nbsp;|&amp;|•|★|✔️|✔|→|➡|–|—|•/g, " ")
    // collapse extra spaces
    .replace(/\s+/g, " ")
    .trim();
};

const ScrapperPage = () => {
  const navigate = useNavigate();
  const [data, setData] = useState([]);
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState({
    from: "",
    to: "",
    connection_from: "",
    connection_type: "",
    keywords: "",
    search_by: "",
  });

  const fetchData = async () => {
    try {
      const res = await getScrapperData(filters);
      setData(res.data || []);
      console.log("Scrapper data:", res.data);
    } catch (err) {
      console.error("Error fetching scrapper data:", err);
    }
  };

  // Handle column sorting
  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  // Sort data based on sortConfig
  const sortedData = React.useMemo(() => {
    if (!sortConfig.key) return data;

    const sorted = [...data].sort((a, b) => {
      const aVal = a[sortConfig.key];
      const bVal = b[sortConfig.key];

      // Handle null/undefined values
      if (aVal == null && bVal == null) return 0;
      if (aVal == null) return sortConfig.direction === 'asc' ? 1 : -1;
      if (bVal == null) return sortConfig.direction === 'asc' ? -1 : 1;

      // Compare values
      if (typeof aVal === 'string') {
        return sortConfig.direction === 'asc'
          ? aVal.localeCompare(bVal)
          : bVal.localeCompare(aVal);
      }

      return sortConfig.direction === 'asc' ? aVal - bVal : bVal - aVal;
    });

    return sorted;
  }, [data, sortConfig]);

  useEffect(() => {
    fetchData();
  }, []);

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters((prev) => ({ ...prev, [name]: value }));
  };

  const handleFilterSubmit = (e) => {
    e.preventDefault();
    fetchData();
  };

  // ---------------- Excel Download Function ----------------
  const downloadExcel = async () => {
    const selectedData = sortedData;
    if (selectedData.length === 0) return alert("No data to download!");

    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet("ScrapperData");

    // Define columns
    const columns = [
      { header: "S.NO", key: "sno", width: 10 },
      { header: "Profile ID", key: "profile_id", width: 14 },
      { header: "Post", key: "post", width: 100 },
      { header: "Designation", key: "Designation", width: 25 },
      { header: "Author", key: "author", width: 20 },
      { header: "Email", key: "email", width: 30 },
      { header: "Is Mail", key: "ISMail", width: 15 },
      { header: "Contact Info", key: "contact_info", width: 25 },
      { header: "Connection Type", key: "connection_type", width: 22 },
      { header: "Profile URL", key: "profile_url", width: 35 },
      { header: "Company", key: "company", width: 25 },
      { header: "Keywords", key: "Keywords", width: 30 },
      { header: "Search By", key: "SearchBy", width: 20 },
      { header: "Unique Post ID", key: "unique_post_id", width: 22 },
      { header: "Connection From", key: "connection_from", width: 22 },
      { header: "Created Date", key: "created_date", width: 25 }
    ];

    worksheet.columns = columns;

    // Format header
    worksheet.getRow(1).eachCell(cell => {
      cell.font = { bold: true, color: { argb: "FFFFFFFF" } };
      cell.fill = {
        type: "pattern",
        pattern: "solid",
        fgColor: { argb: "FF0070C0" }
      };
      cell.alignment = { vertical: "middle", horizontal: "center" };
      cell.border = {
        top: { style: "thin" },
        left: { style: "thin" },
        bottom: { style: "thin" },
        right: { style: "thin" }
      };
    });

    // Add data rows
    selectedData.forEach((row, idx) => {
      const cleanPost = cleanText(row.post);

      const newRow = worksheet.addRow({
        sno: idx + 1,
        profile_id: row.profile_id,
        post: cleanPost,
        Designation: row.Designation,
        author: row.author,
        email: row.email,
        ISMail: row.ISMail ? "Yes" : "No",
        contact_info: row.contact_info,
        connection_type: row.connection_type,
        profile_url: row.profile_url,
        company: row.company,
        Keywords: row.Keywords || "",
        SearchBy: row.SearchBy || "",
        unique_post_id: row.unique_post_id,
        connection_from: row.connection_from,
        created_date: row.created_date
          ? new Date(row.created_date).toLocaleString()
          : ""
      });

      // AUTO HEIGHT BASED ON TEXT LENGTH
      const approxLines = Math.ceil(cleanPost.length / 60);
      newRow.height = Math.max(20, approxLines * 15);
    });

    // Format table rows
    worksheet.eachRow((row, rowNumber) => {
      if (rowNumber === 1) return;

      row.eachCell(cell => {
        cell.alignment = { vertical: "top", horizontal: "center", wrapText: true };
        cell.border = {
          top: { style: "thin" },
          left: { style: "thin" },
          bottom: { style: "thin" },
          right: { style: "thin" }
        };
      });
    });

    worksheet.autoFilter = {
      from: { row: 1, column: 1 },
      to: { row: 1, column: columns.length }
    };

    // Download file
    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
    });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "scrapper-data.xlsx";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  };

  return (
    <div className="scrapper-page-wrapper">

      <div className="scrapper-header-section">
        <h1 className="scrapper-title">Scrapper Data Dashboard</h1>
      </div>

      {/* Filter Buttons */}
      {!showFilters && (
        <div className="filter-toggle-buttons-container">
          <button 
            className="filter-toggle-show-btn"
            onClick={() => setShowFilters(true)}
            type="button"
          >
            Show Filters
          </button>
        </div>
      )}

      {/* Filters */}
      {showFilters && (
        <>
          <form onSubmit={handleFilterSubmit} className="scrapper-filter-box">
        <div className="scrapper-filter-group">
          <label className="scrapper-label">From Date</label>
          <input
            type="date"
            name="from"
            value={filters.from}
            onChange={handleFilterChange}
            className="scrapper-input"
          />
        </div>

        <div className="scrapper-filter-group">
          <label className="scrapper-label">To Date</label>
          <input
            type="date"
            name="to"
            value={filters.to}
            onChange={handleFilterChange}
            className="scrapper-input"
          />
        </div>

        <div className="scrapper-filter-group">
          <label className="scrapper-label">Connection From</label>
          <input
            type="text"
            name="connection_from"
            placeholder="e.g., LinkedIn"
            value={filters.connection_from}
            onChange={handleFilterChange}
            className="scrapper-input"
          />
        </div>

        <div className="scrapper-filter-group">
          <label className="scrapper-label">Connection Type</label>
          <input
            type="text"
            name="connection_type"
            placeholder="e.g., Direct"
            value={filters.connection_type}
            onChange={handleFilterChange}
            className="scrapper-input"
          />
        </div>

        <div className="scrapper-filter-group">
          <label className="scrapper-label">Keywords</label>
          <input
            type="text"
            name="keywords"
            placeholder="e.g., AI, Machine Learning"
            value={filters.keywords}
            onChange={handleFilterChange}
            className="scrapper-input"
          />
        </div>

        <div className="scrapper-filter-group">
          <label className="scrapper-label">Search By</label>
          <input
            type="text"
            name="search_by"
            placeholder="e.g., LinkedIn, Google"
            value={filters.search_by}
            onChange={handleFilterChange}
            className="scrapper-input"
          />
        </div>

        <button type="submit" className="scrapper-filter-btn">
          Filter
        </button>

        <button className="scrapper-download-btn" onClick={downloadExcel}>
          Download Excel
        </button>
      </form>

      <div className="filter-toggle-buttons-container">
        <button 
          className="filter-toggle-hide-btn"
          onClick={() => setShowFilters(false)}
          type="button"
        >
          Hide Filters
        </button>
        <button
          className="filter-toggle-hide-btn"
          type="button"
          onClick={() => navigate('/mailer')}
        >
          Go to Mails
        </button>
      </div>
        </>
      )}

      <div className="scrapper-table-wrapper">
        <table className="scrapper-table">
          <thead>
            <tr>
              {[
                { label: "S.NO", key: "sno" },
                { label: "Profile ID", key: "profile_id", className: "profileid-column" },
                { label: "Post", key: "post", className: "post-column" },
                { label: "Designation", key: "Designation", className: "designation-column" },
                { label: "Author", key: "author" },
                { label: "Email", key: "email", className: "email-column" },
                { label: "IsMail", key: "ISMail", className: "ismail-column" },
                { label: "Contact Info", key: "contact_info" },
                { label: "Connection Type", key: "connection_type", className: "connectiontype-column" },
                { label: "Profile URL", key: "profile_url" },
                { label: "Company", key: "company", className: "company-column" },
                { label: "Keywords", key: "Keywords" },
                { label: "Search By", key: "SearchBy" },
                { label: "Unique Post ID", key: "unique_post_id" },
                { label: "Connection From", key: "connection_from" },
                { label: "Created Date", key: "created_date" },
              ].map((col) => (
                <th 
                  key={col.key} 
                  className={`scrapper-th scrapper-th-sortable-header ${col.className || ''}`}
                  onClick={() => handleSort(col.key)}
                  title="Click to sort"
                >
                  {col.label}
                  {sortConfig.key === col.key ? (
                    <span className="scrapper-sort-arrow">
                      {sortConfig.direction === 'asc' ? '' : ''}
                    </span>
                  ) : (
                    <span className="scrapper-sort-unsorted-arrow"></span>
                  )}
                </th>
              ))}
            </tr>
          </thead>

          <tbody>
            {sortedData.length === 0 ? (
              <tr>
                <td colSpan={16} className="scrapper-no-data">
                  No data found.
                </td>
              </tr>
            ) : (
              sortedData.map((row, idx) => {
                return (
                  <tr key={`${row.profile_id || idx}-${idx}`} className="scrapper-tr-hover">
                    <td className="scrapper-td scrapper-td-center-bold">
                      {idx + 1}
                    </td>

                    <td className="scrapper-td profileid-column">{row.profile_id}</td>

                  <td className="scrapper-td scrapper-td-post-content post-column">
                    {cleanText(row.post)}
                  </td>

                  <td className="scrapper-td designation-column">{row.Designation}</td>
                  <td className="scrapper-td">{row.author}</td>
                  <td className="scrapper-td email-column">{row.email}</td>

                  <td className="scrapper-td scrapper-td-center ismail-column">
                    {row.ISMail ? "✔️" : ""}
                  </td>

                  <td className="scrapper-td">{row.contact_info}</td>
                  <td className="scrapper-td connectiontype-column">{row.connection_type}</td>

                  <td className="scrapper-td">
                    <a href={row.profile_url} target="_blank" rel="noreferrer" className="scrapper-link">
                      View Profile
                    </a>
                  </td>

                  <td className="scrapper-td">{row.company}</td>
                  <td className="scrapper-td">{row.Keywords || "N/A"}</td>
                  <td className="scrapper-td">{row.SearchBy || "N/A"}</td>
                  <td className="scrapper-td">{row.unique_post_id}</td>
                  <td className="scrapper-td">{row.connection_from}</td>

                  <td className="scrapper-td">
                    {row.created_date ? new Date(row.created_date).toLocaleString() : ""}
                  </td>
                </tr>
              );
            })
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default ScrapperPage;
